/**
 * Bộ chọn công ty CHUNG ở đầu dashboard — lọc mọi widget cùng lúc thay vì chỉnh từng widget.
 *
 *   - Mặc định: "công ty đang đăng nhập" (giá trị `null`) — dashboard chạy y như trước, widget nào đã
 *     khai `widgetConfig.id_apps` riêng thì vẫn theo khai báo đó.
 *   - Chọn công ty khác / nhiều công ty: mọi widget hỗ trợ (counter, danh sách, báo cáo, pivot, trang
 *     tuỳ biến) lấy dữ liệu của các công ty đã chọn; thẻ đếm cộng tổng, widget còn lại có thanh chọn công ty.
 *   - Lựa chọn là của TỪNG NGƯỜI XEM (lưu trình duyệt theo công ty đang đăng nhập + mã dashboard), không
 *     ghi vào dashboardconfig — người khác mở cùng dashboard vẫn thấy mặc định.
 *
 * Chỉ hiện khi người dùng tham gia từ 2 công ty trở lên. Logic thuần ở dashboardCongTy.js.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonBase, InputBase, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import AuthContext from './auth/Context';
import ManagedDropdown from './ManagedDropdown';
import { useDsCongTy } from './PhamViCongTy';
import { chuanHoaLocCongTy, khoaLuuLocCongTy } from './dashboardCongTy';

const docLuu = (khoa) => {
	try {
		const v = JSON.parse(window.localStorage.getItem(khoa) || 'null');
		return Array.isArray(v) ? v : null;
	} catch (e) { return null; }
};
const ghiLuu = (khoa, v) => {
	try {
		if (v) window.localStorage.setItem(khoa, JSON.stringify(v));
		else window.localStorage.removeItem(khoa);
	} catch (e) { /* trình duyệt chặn lưu -> chỉ nhớ trong phiên */ }
};

/**
 * Trạng thái bộ lọc của một dashboard. `locCongTy` đưa vào LocCongTyContext; `datLoc(ids)` đổi lựa chọn.
 * @param {{ maDashboard: string, batDau?: boolean }} opts  batDau=false -> tắt hẳn (dashboard nhúng/biên tập)
 */
export function useLocCongTyDashboard({ maDashboard, batDau = true }) {
	const context = useContext(AuthContext);
	const goc = context?.nguCanhGoc || context;
	const idHienTai = goc?.appInfo?._id ? String(goc.appInfo._id) : '';
	const { dsCongTy, isLoading } = useDsCongTy(batDau);
	const khoa = khoaLuuLocCongTy(idHienTai, maDashboard);

	const [chon, setChon] = useState(null);
	// Đổi công ty đăng nhập / đổi dashboard -> nạp lại lựa chọn đã lưu của đúng cặp đó.
	useEffect(() => { setChon(batDau && idHienTai ? docLuu(khoa) : null); }, [khoa, batDau, idHienTai]);

	// Lựa chọn đã lưu mà nay không còn quyền vào công ty đó thì bỏ (chỉ khi danh sách đã tải xong).
	const locCongTy = useMemo(
		() => (batDau ? chuanHoaLocCongTy(chon, idHienTai, isLoading ? null : dsCongTy.map((c) => c._id)) : null),
		[batDau, chon, idHienTai, isLoading, dsCongTy]
	);

	const datLoc = useCallback((ds) => {
		const v = chuanHoaLocCongTy(ds, idHienTai);
		setChon(v);
		ghiLuu(khoa, v);
	}, [idHienTai, khoa]);

	return { locCongTy, datLoc, dsCongTy, idHienTai, tenHienTai: goc?.appInfo?.name || '', hien: batDau && dsCongTy.length >= 2 };
}

/** Chữ cái đầu của tên công ty cho ô biểu tượng tròn (bỏ "Công ty", "TNHH"… ở đầu). */
const chuDau = (ten) => {
	const bo = /^(công ty|cty|tnhh|cổ phần|cp|mtv|tập đoàn|chi nhánh|cn)\s+/i;
	let t = String(ten || '').trim();
	for (let k = 0; k < 4 && bo.test(t); k++) t = t.replace(bo, '');
	return (t[0] || '?').toUpperCase();
};

const NGUONG_TIM = 8; // từ 9 công ty trở lên mới cần ô tìm

/** Câu giải thích lựa chọn hiện tại — hiện ở lời nhắc của nút và đầu bảng chọn (thay dòng chú thích cũ). */
const moTaLoc = (locCongTy) => {
	if (!locCongTy) return 'Đang xem dữ liệu của công ty đang đăng nhập. Bấm để xem công ty khác.';
	if (locCongTy.length > 1) return `Đang xem ${locCongTy.length} công ty: thẻ đếm cộng tổng, widget khác có thanh chọn công ty.`;
	return 'Mọi widget đang hiện dữ liệu của công ty đã chọn.';
};

/**
 * Nút chọn công ty — gọn để đặt CÙNG HÀNG tiêu đề dashboard (canh phải), không chiếm dòng riêng.
 *   gon  -> chỉ còn nút tròn biểu tượng (+ số công ty khi đang lọc) — màn hình hẹp.
 *   noi  -> nền trắng + bóng đổ như thanh công cụ — khi nổi trên nội dung (dashboard ẩn tiêu đề).
 */
export default function ThanhLocCongTy({ locCongTy, datLoc, dsCongTy, idHienTai, tenHienTai, hien, sx, gon = false, noi = false }) {
	const [neo, setNeo] = useState(null);
	const [tim, setTim] = useState('');

	const options = useMemo(() => {
		const ds = dsCongTy.map((c) => ({ ...c, hienTai: c._id === idHienTai }));
		if (idHienTai && !ds.some((c) => c.hienTai)) ds.unshift({ _id: idHienTai, name: tenHienTai || idHienTai, hienTai: true });
		return ds.sort((a, b) => (b.hienTai ? 1 : 0) - (a.hienTai ? 1 : 0));
	}, [dsCongTy, idHienTai, tenHienTai]);

	const dangChon = useMemo(() => locCongTy || (idHienTai ? [idHienTai] : []), [locCongTy, idHienTai]);
	const ten = useCallback((id) => (options.find((o) => o._id === id) || {}).name || id, [options]);
	const loc = useMemo(() => {
		const q = tim.trim().toLowerCase();
		return q ? options.filter((o) => String(o.name || '').toLowerCase().includes(q)) : options;
	}, [options, tim]);

	if (!hien) return null;
	const macDinh = !locCongTy;

	const batTat = (id) => {
		const co = dangChon.includes(id);
		datLoc(co ? dangChon.filter((x) => x !== id) : [...dangChon, id]);
	};

	const nenNut = (t, hover) => {
		if (!macDinh) return alpha(t.palette.primary.main, hover ? 0.16 : 0.1);
		if (noi) return hover ? '#f5f5f7' : 'rgba(255,255,255,0.95)';
		return hover ? 'rgba(0,0,0,0.07)' : 'rgba(0,0,0,0.04)';
	};
	const kieuNut = (t) => ({
		display: 'inline-flex', alignItems: 'center', gap: 1, maxWidth: '100%', minWidth: 0,
		borderRadius: 999, fontSize: '0.8125rem', lineHeight: 1.4,
		bgcolor: nenNut(t, false),
		color: macDinh ? 'text.primary' : 'primary.main',
		transition: 'background-color .15s',
		...(noi ? { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.05)', backdropFilter: 'blur(10px)' } : {}),
		'&:hover': { bgcolor: nenNut(t, true) },
		'&.Mui-focusVisible': { outline: `2px solid ${alpha(t.palette.primary.main, 0.4)}` },
	});
	const oBieuTuong = (co) => (t) => ({
		width: co, height: co, borderRadius: '50%', flexShrink: 0,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		bgcolor: macDinh ? 'background.paper' : t.palette.primary.main,
		color: macDinh ? 'text.secondary' : t.palette.primary.contrastText,
		boxShadow: macDinh ? '0 0 0 1px rgba(0,0,0,0.06)' : 'none',
	});

	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', ...sx }}>
			<Tooltip title={neo ? '' : moTaLoc(locCongTy)} arrow placement="bottom-end">
				{gon ? (
					// Màn hình hẹp: nút tròn cỡ nút cài đặt; đang lọc thì có số công ty.
					<ButtonBase onClick={(e) => setNeo(e.currentTarget)} aria-haspopup="dialog" aria-label="Chọn công ty lấy dữ liệu"
						sx={(t) => ({ ...kieuNut(t), p: 0.5, position: 'relative' })}>
						<Box sx={oBieuTuong(28)}><BusinessIcon sx={{ fontSize: 16 }} /></Box>
						{!macDinh && (
							<Box component="span" sx={{
								position: 'absolute', top: -7, right: -9, minWidth: 18, height: 18, px: 0.5, borderRadius: 999,
								display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
								fontSize: '0.68rem', fontWeight: 700, lineHeight: 1, border: '2px solid #fff',
								bgcolor: 'primary.main', color: 'primary.contrastText',
							}}>{dangChon.length}</Box>
						)}
					</ButtonBase>
				) : (
					<ButtonBase onClick={(e) => setNeo(e.currentTarget)} aria-haspopup="dialog"
						sx={(t) => ({ ...kieuNut(t), pl: 0.5, pr: 1, py: 0.5 })}>
						<Box sx={oBieuTuong(24)}><BusinessIcon sx={{ fontSize: 14 }} /></Box>
						<Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: 220 }}>
							{ten(dangChon[0])}
						</Box>
						{dangChon.length > 1 && (
							<Box component="span" sx={{ flexShrink: 0, px: 0.75, height: 18, display: 'inline-flex', alignItems: 'center', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
								+{dangChon.length - 1}
							</Box>
						)}
						<ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0, opacity: 0.6, transition: 'transform .2s', transform: neo ? 'rotate(180deg)' : 'none' }} />
					</ButtonBase>
				)}
			</Tooltip>

			<ManagedDropdown
				type="popover"
				open={!!neo}
				anchorEl={neo}
				onClose={() => { setNeo(null); setTim(''); }}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				sx={{ '& .MuiPopover-paper': { mt: 1, width: 320, maxWidth: 'calc(100vw - 32px)', borderRadius: 3, boxShadow: '0 12px 40px rgba(0,0,0,0.14)', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' } }}
			>
				<Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
					<Typography variant="subtitle2" fontWeight={700}>Xem dữ liệu của công ty</Typography>
					<Typography variant="caption" color="text.secondary" component="div">Áp dụng cho mọi widget trên dashboard này</Typography>
					{!macDinh && <Typography variant="caption" color="primary.main" component="div" sx={{ mt: 0.25 }}>{moTaLoc(locCongTy)}</Typography>}
				</Box>

				{options.length > NGUONG_TIM && (
					<Box sx={{ mx: 1.5, mb: 1, px: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.04)' }}>
						<SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
						<InputBase autoFocus fullWidth value={tim} onChange={(e) => setTim(e.target.value)} placeholder="Tìm công ty…" sx={{ fontSize: '0.875rem', py: 0.5 }} />
					</Box>
				)}

				<Box sx={{ maxHeight: 320, overflowY: 'auto', px: 0.75, pb: 0.75 }}>
					{loc.map((o) => {
						const co = dangChon.includes(o._id);
						return (
							<ButtonBase
								key={o._id}
								onClick={() => batTat(o._id)}
								sx={(t) => ({
									width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.9,
									borderRadius: 2, textAlign: 'left',
									bgcolor: co ? alpha(t.palette.primary.main, 0.08) : 'transparent',
									'&:hover': { bgcolor: co ? alpha(t.palette.primary.main, 0.12) : 'rgba(0,0,0,0.04)' },
								})}
							>
								<Box sx={(t) => ({
									width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
									display: 'flex', alignItems: 'center', justifyContent: 'center',
									fontSize: '0.8rem', fontWeight: 700,
									bgcolor: co ? t.palette.primary.main : 'rgba(0,0,0,0.06)',
									color: co ? t.palette.primary.contrastText : 'text.secondary',
									transition: 'all .15s',
								})}>
									{co ? <CheckIcon sx={{ fontSize: 17 }} /> : chuDau(o.name)}
								</Box>
								<Box sx={{ minWidth: 0, flex: 1 }}>
									<Typography variant="body2" fontWeight={co ? 600 : 400} noWrap>{o.name || o._id}</Typography>
									{o.hienTai && <Typography variant="caption" color="text.secondary" component="div">Đang đăng nhập</Typography>}
								</Box>
							</ButtonBase>
						);
					})}
					{loc.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2, textAlign: 'center' }}>Không có công ty nào khớp</Typography>
					)}
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
					<Button size="small" disabled={macDinh} onClick={() => datLoc(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
						Về công ty đang đăng nhập
					</Button>
					<Button size="small" variant="contained" disableElevation onClick={() => { setNeo(null); setTim(''); }} sx={{ textTransform: 'none', borderRadius: 999, px: 2 }}>
						Xong
					</Button>
				</Box>
			</ManagedDropdown>
		</Box>
	);
}

ThanhLocCongTy.propTypes = {
	locCongTy: PropTypes.arrayOf(PropTypes.string),
	datLoc: PropTypes.func.isRequired,
	dsCongTy: PropTypes.array.isRequired,
	idHienTai: PropTypes.string,
	tenHienTai: PropTypes.string,
	hien: PropTypes.bool,
	sx: PropTypes.object,
};
